import type { ReactNode } from "react";
import { Icon, type IconProps } from "./IconBase";

const make = (paths: ReactNode) => (props: IconProps) => <Icon {...props}>{paths}</Icon>;

export const I = {
  search: make(<path d="M11 19a8 8 0 1 1 0-16 8 8 0 0 1 0 16Zm6.5-2 4.5 4.5" />),
  globe: make(
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" />
    </>,
  ),
  menu: make(<path d="M4 7h16M4 12h16M4 17h16" />),
  bell: make(<path d="M6 16V11a6 6 0 1 1 12 0v5l1.5 2H4.5L6 16Zm4 3a2 2 0 0 0 4 0" />),
  plus: make(<path d="M12 5v14M5 12h14" />),
  minus: make(<path d="M5 12h14" />),
  check: make(<path d="M5 13l4 4L19 7" />),
  x: make(<path d="M6 6l12 12M18 6 6 18" />),
  arrow: make(<path d="M5 12h14M13 6l6 6-6 6" />),
  // A picture frame struck through: media that can no longer be shown.
  imageOff: make(
    <>
      <path d="M3 3l18 18" />
      <path d="M9 4h10a2 2 0 0 1 2 2v10M20 19.4a2 2 0 0 1-1 .6H5a2 2 0 0 1-2-2V6a2 2 0 0 1 .6-1.4" />
      <path d="M3 16l5-5 3 3M14.5 12.5 16 11l5 5" />
    </>,
  ),
  // A three-quarter arc, so the rotation reads. Pair with `animate-spin`.
  spinner: make(<path d="M12 3a9 9 0 1 0 9 9" />),
  back: make(<path d="M19 12H5M11 6 5 12l6 6" />),
  down: make(<path d="M6 9l6 6 6-6" />),
  home: make(<path d="M4 11 12 4l8 7v9h-5v-6h-6v6H4v-9Z" />),
  chat: make(<path d="M4 6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H9l-4 3v-3H6a2 2 0 0 1-2-2V6Z" />),
  users: make(
    <>
      <circle cx="9" cy="9" r="3.2" />
      <path d="M3 19c.6-3 3-4.5 6-4.5s5.4 1.5 6 4.5" />
      <circle cx="17" cy="8" r="2.6" />
      <path d="M15 13.6c2.4-.4 5 .9 5.5 4.4" />
    </>,
  ),
  user: make(
    <>
      <circle cx="12" cy="9" r="3.5" />
      <path d="M5 19c.8-3.5 3.6-5 7-5s6.2 1.5 7 5" />
    </>,
  ),
  cog: make(
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M19 12c0 .8-.1 1.5-.3 2.2l1.7 1.3-2 3.4-2-.7c-1.1.9-2.4 1.5-3.8 1.8L13 22h-4l-.6-2c-1.4-.3-2.7-.9-3.8-1.8l-2 .7-2-3.4 1.7-1.3A8 8 0 0 1 4.7 12L3 10.7l2-3.4 2 .7c1.1-.9 2.4-1.5 3.8-1.8L11 4h2l.6 2c1.4.3 2.7.9 3.8 1.8l2-.7 2 3.4-1.7 1.3c.2.7.3 1.4.3 2.2Z" />
    </>,
  ),
  card: make(
    <>
      <rect x="3" y="6" width="18" height="13" rx="2" />
      <path d="M3 11h18" />
    </>,
  ),
  shield: make(<path d="M12 3 4 6v6c0 4 3.5 7.5 8 9 4.5-1.5 8-5 8-9V6l-8-3Z" />),
  zap: make(<path d="M13 3 4 14h6l-1 7 9-11h-6l1-7Z" />),
  paperplane: make(<path d="M3 11.5 21 4l-7 17-3-7-8-2.5Z" />),
  star: make(<path d="M12 4l2.5 5.2 5.7.6-4.3 4 1.2 5.7L12 16.6 7 19.5l1.2-5.7L4 9.8l5.7-.6L12 4Z" />),
  filter: make(<path d="M4 6h16M7 12h10M10 18h4" />),
  attach: make(<path d="M8 12V7a4 4 0 0 1 8 0v9a3 3 0 1 1-6 0V8a2 2 0 0 1 4 0v8" />),
  smile: make(
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M8.5 14c1 1.5 5 1.5 7 0M9 10h.01M15 10h.01" />
    </>,
  ),
  qr: make(
    <>
      <rect x="3" y="3" width="7" height="7" />
      <rect x="14" y="3" width="7" height="7" />
      <rect x="3" y="14" width="7" height="7" />
      <path d="M14 14h3v3M20 14v3M14 20h3M20 17v4" />
    </>,
  ),
  building: make(
    <>
      <rect x="4" y="3" width="16" height="18" rx="1" />
      <path d="M9 7h2M9 11h2M9 15h2M13 7h2M13 11h2M13 15h2M10 21v-4h4v4" />
    </>,
  ),
  inbox: make(<path d="M3 13l3-8h12l3 8v6a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-6Zm0 0h5l1 2h6l1-2h5" />),
  box: make(
    <>
      <path d="M3 7l9-4 9 4v10l-9 4-9-4V7Z" />
      <path d="M3 7l9 4 9-4M12 11v10" />
    </>,
  ),
  close: make(<path d="M6 6l12 12M18 6 6 18" />),
  receipt: make(
    <>
      <path d="M6 3h12v18l-3-2-3 2-3-2-3 2V3Z" />
      <path d="M9 8h6M9 12h6M9 16h4" />
    </>,
  ),
  truck: make(
    <>
      <path d="M3 6h11v9H3V6Z" />
      <path d="M14 9h3.5l2.5 3v3H14V9Z" />
      <circle cx="7" cy="17.5" r="1.6" />
      <circle cx="17" cy="17.5" r="1.6" />
    </>,
  ),
};
